import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import Swal from 'sweetalert2';

// Normalize with exactly one trailing slash
const API_BASE = ((process.env.REACT_APP_BASE_URL || "http://localhost:5000/")
  .replace(/\/$/, '')) + '/';

const AdminStories = () => {
  const [stories, setStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentStory, setCurrentStory] = useState(null);

  // Local form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    imageFile: null,
    imagePreview: ''
  });

  // Fetch on mount
  useEffect(() => {
    fetchCategories();
    fetchStories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories(data);
    } catch {
      Swal.fire('Error', 'Failed to load categories', 'error');
    }
  };

  const fetchStories = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/stories');
      setStories(data);
    } catch {
      Swal.fire('Error', 'Failed to load stories', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openAddModal = () => {
    setFormData({
      title: '',
      description: '',
      category_id: '',
      imageFile: null,
      imagePreview: ''
    });
    setEditMode(false);
    setCurrentStory(null);
    setIsModalOpen(true);
  };

  const openEditModal = (story) => {
    setFormData({
      title: story.title,
      description: story.description || '',
      category_id: story.category_id?.toString() || '',
      imageFile: null,
      imagePreview: story.image_path || ''
    });
    setEditMode(true);
    setCurrentStory(story);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentStory(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(fd => ({
      ...fd,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setFormData(fd => ({
      ...fd,
      imageFile: file,
      imagePreview: URL.createObjectURL(file)
    }));
  };

  // 🟣 MAIN CHANGE: All-in-one FormData submit (for add + edit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.title.trim() || !formData.category_id) {
      return Swal.fire('Validation Error', 'Title & Category are required.', 'error');
    }
    if (!editMode && !formData.imageFile) {
      return Swal.fire('Validation Error', 'Image is required.', 'error');
    }

    try {
      const submitForm = new FormData();
      submitForm.append('title', formData.title.trim());
      submitForm.append('description', formData.description.trim() || '');
      submitForm.append('category_id', formData.category_id);

      // Only append image if selected (for new OR edit)
      if (formData.imageFile) {
        submitForm.append('image', formData.imageFile); // MUST be 'image'
      }

      if (editMode && currentStory) {
        // For edit, call PUT with multipart/form-data
        await api.put(`/stories/${currentStory.id}`, submitForm, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        Swal.fire({ icon: 'success', title: 'Story updated', timer: 1200, showConfirmButton: false });
      } else {
        // For add
        await api.post('/stories', submitForm, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        Swal.fire({ icon: 'success', title: 'Story added', timer: 1200, showConfirmButton: false });
      }

      closeModal();
      fetchStories();
    } catch (err) {
      // Show backend error
      const msg = err?.response?.data?.message || 'Failed to save story';
      Swal.fire('Error', msg, 'error');
    }
  };

  const handleDelete = (story) => {
    Swal.fire({
      title: `Delete "${story.title}"?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/stories/${story.id}`);
          Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
          fetchStories();
        } catch {
          Swal.fire('Error', 'Failed to delete story', 'error');
        }
      }
    });
  };

  return (
    <div className="p-6">
      {/* Header Bar */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Story Updates</h1>
        <button
          onClick={openAddModal}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition"
        >
          Add New Story
        </button>
      </div>
      {/* Stories Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center">
                  <div className="flex justify-center items-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                    <span>Loading stories...</span>
                  </div>
                </td>
              </tr>
            ) : stories.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No stories found.
                </td>
              </tr>
            ) : (
              stories.map((story) => {
                const raw = story.image_path || '';
                const thumbnailUrl =
                  raw.startsWith('http')
                    ? raw
                    : raw.length > 0
                    ? `${API_BASE}${raw.replace(/^\/+/, '')}`
                    : '/placeholder.png';

                return (
                  <tr key={story.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-12 w-12 rounded overflow-hidden bg-gray-100">
                        <img
                          src={thumbnailUrl}
                          alt={story.title}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {story.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {story.category_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 line-clamp-2">
                      {story.description || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center space-x-2">
                      <button
                        onClick={() => openEditModal(story)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >Edit</button>
                      <button
                        onClick={() => handleDelete(story)}
                        className="text-red-600 hover:text-red-900"
                      >Delete</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">
                  {editMode ? 'Edit Story' : 'Add New Story'}
                </h2>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">×</button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full p-2 border rounded focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image {editMode ? '(leave blank to keep current)' : '*'}
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.imagePreview ? (
                      <div className="h-16 w-16 rounded overflow-hidden bg-gray-100">
                        <img
                          src={
                            formData.imagePreview.startsWith('blob:')
                              ? formData.imagePreview
                              : formData.imagePreview.startsWith('http')
                              ? formData.imagePreview
                              : `${API_BASE}formData.imagePreview`
                          }
                          alt="Preview"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="h-16 w-16 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-gray-500 text-xs">No image</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="block text-sm text-gray-500
                                file:mr-4 file:py-2 file:px-4 file:rounded
                                file:border-0 file:text-sm file:font-semibold
                                file:bg-black file:text-white hover:file:bg-gray-900"
                      required={!editMode}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
                    disabled={isLoading}
                  >Cancel</button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-black text-white rounded hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >{editMode ? 'Update Story' : 'Add Story'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStories;
